import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COLOR_ESTADO_FACTURA,
  ETIQUETA_ESTADO_FACTURA,
  formatearMonedaArs,
  type FacturaDTO,
} from '@ausentia/shared';
import { abrirPdf, api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';

/**
 * Comprobantes que los médicos le emiten al operador por sus honorarios. El
 * operador los recibe y los marca pagados (ADR-0010).
 */
export function ComprobantesMedicos() {
  const toast = useToast();
  const [facturas, setFacturas] = useState<FacturaDTO[] | null>(null);
  const [error, setError] = useState('');

  const refrescar = useCallback(async () => {
    try {
      setFacturas(await api.get<FacturaDTO[]>('/api/operador/comprobantes-medico'));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar los comprobantes');
    }
  }, []);

  useEffect(() => { void refrescar(); }, [refrescar]);

  async function pagar(id: string) {
    try {
      await api.post(`/api/operador/comprobantes-medico/${id}/pagar`);
      await refrescar();
      toast.mostrar('Comprobante marcado como pagado');
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo actualizar');
    }
  }

  async function verPdf(id: string) {
    try {
      await abrirPdf(`/api/operador/comprobantes-medico/${id}/pdf`);
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : String(err));
    }
  }

  const kpis = useMemo(() => {
    const activas = (facturas ?? []).filter((f) => f.estado !== 'ANULADA');
    const total = activas.reduce((s, f) => s + f.totalCentavos, 0);
    const pagado = activas.filter((f) => f.estado === 'PAGADA').reduce((s, f) => s + f.totalCentavos, 0);
    return { total, pagado, pendiente: total - pagado };
  }, [facturas]);

  return (
    <section>
      <h2>Comprobantes de médicos</h2>
      <p className="muted">
        Facturas que los profesionales emiten por sus honorarios. Revisalas y marcalas como pagadas
        cuando liquides el pago.
      </p>

      {error && <div className="error-box" role="status">{error}</div>}

      <div className="grid-tarjetas" style={{ marginTop: 6 }}>
        <div className="kpi">
          <span className="kpi-rotulo">A pagar</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.total)}</span>
        </div>
        <div className="kpi kpi-ganancia">
          <span className="kpi-rotulo">Pagado</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.pagado)}</span>
        </div>
        <div className="kpi">
          <span className="kpi-rotulo">Pendiente de pago</span>
          <span className="kpi-monto">{formatearMonedaArs(kpis.pendiente)}</span>
        </div>
      </div>

      <h3 style={{ marginTop: 24 }}>Comprobantes recibidos</h3>
      {facturas === null ? (
        <div className="cargando">Cargando…</div>
      ) : facturas.length === 0 ? (
        <p className="muted">Todavía no recibiste comprobantes de los médicos.</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th scope="col">Número</th>
              <th scope="col">Profesional</th>
              <th scope="col">Período</th>
              <th scope="col">Visitas</th>
              <th scope="col">Total</th>
              <th scope="col">Estado</th>
              <th scope="col" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {facturas.map((f) => (
              <tr key={f.id}>
                <td><span className="mini" style={{ fontFamily: 'monospace' }}>{f.numero}</span></td>
                <td>{f.contraparteNombre}</td>
                <td className="mini">{f.periodo.desde} → {f.periodo.hasta}</td>
                <td>{f.visitas}</td>
                <td>{formatearMonedaArs(f.totalCentavos)}</td>
                <td><span className={`insignia ${COLOR_ESTADO_FACTURA[f.estado]}`}>{ETIQUETA_ESTADO_FACTURA[f.estado]}</span></td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {f.estado === 'EMITIDA' && (
                      <button className="secundario" style={{ width: 'auto' }} onClick={() => void pagar(f.id)}>Marcar pagada</button>
                    )}
                    <button className="secundario" style={{ width: 'auto' }} onClick={() => void verPdf(f.id)}>PDF</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
